import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook for Server-Sent Events using fetch + ReadableStream.
 * Uses Bearer token from axios defaults (EventSource doesn't support custom headers).
 *
 * @param {string} path - API path (e.g. '/admin/hermes/backup/stream')
 * @param {object} params - Query params (e.g. { host_ids: '1,2,3' })
 * @param {boolean} enabled - Whether to connect
 * @returns {{ data, connected, error, reconnect }}
 */
export default function useSSE(path, params = {}, enabled = true) {
    const [data, setData] = useState(null);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    const retryRef = useRef(0);
    const mountedRef = useRef(true);

    const connect = useCallback(() => {
        // `path` vazio nao e caso de erro, e caso de "ainda nao ha o que ouvir".
        //
        // Sem esta guarda, um path nulo virava a URL `baseURL + null` — literalmente
        // /api/v1null — que responde 404, cai no catch, agenda reconexao e tenta de
        // novo. Cada tentativa chama setConnected/setError, o que re-renderiza a
        // tela inteira: o efeito visivel e a pagina piscando sem ninguem mexer nela.
        // Quem chama passa um path que so existe enquanto ha algo acontecendo, e
        // isso precisa significar "nao conecte", nao "conecte em lugar nenhum".
        if (!enabled || !path || !mountedRef.current) return;

        // Build URL with query params
        const baseURL = api.defaults.baseURL;
        const url = new URL(baseURL + path, window.location.origin);
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') {
                url.searchParams.set(k, v);
            }
        });

        // Get token from axios defaults
        const token = api.defaults.headers.common['Authorization'];

        const controller = new AbortController();
        abortRef.current = controller;

        fetch(url.toString(), {
            headers: {
                'Authorization': token,
                'Accept': 'text/event-stream',
            },
            signal: controller.signal,
        })
            .then(response => {
                if (!response.ok) throw new Error(`SSE ${response.status}`);
                if (!mountedRef.current) return;

                setConnected(true);
                setError(null);
                retryRef.current = 0;

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                const read = () => {
                    reader.read().then(({ done, value }) => {
                        if (done || !mountedRef.current) {
                            setConnected(false);
                            scheduleReconnect();
                            return;
                        }

                        buffer += decoder.decode(value, { stream: true });
                        const lines = buffer.split('\n');
                        buffer = lines.pop(); // Keep incomplete line in buffer

                        for (const line of lines) {
                            if (line.startsWith('data:')) {
                                try {
                                    const parsed = JSON.parse(line.slice(5).trim());
                                    setData(parsed);
                                } catch (e) {
                                    // Not JSON, ignore
                                }
                            }
                        }

                        read();
                    }).catch(err => {
                        if (err.name !== 'AbortError' && mountedRef.current) {
                            setConnected(false);
                            scheduleReconnect();
                        }
                    });
                };

                read();
            })
            .catch(err => {
                if (err.name === 'AbortError') return;
                if (!mountedRef.current) return;
                setConnected(false);
                setError(err.message);
                scheduleReconnect();
            });
    }, [enabled, path, JSON.stringify(params)]);

    const scheduleReconnect = useCallback(() => {
        if (!mountedRef.current) return;
        const delay = Math.min(1000 * Math.pow(2, retryRef.current), 30000);
        retryRef.current++;
        setTimeout(() => {
            if (mountedRef.current) connect();
        }, delay);
    }, [connect]);

    const disconnect = useCallback(() => {
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
        setConnected(false);
    }, []);

    const reconnect = useCallback(() => {
        disconnect();
        retryRef.current = 0;
        connect();
    }, [disconnect, connect]);

    useEffect(() => {
        mountedRef.current = true;
        if (enabled && path) connect();

        return () => {
            mountedRef.current = false;
            disconnect();
        };
    }, [enabled, path, connect, disconnect]);

    return { data, connected, error, reconnect, disconnect };
}
